import {
  Component,
  inject,
  ChangeDetectionStrategy,
  computed,
  OnDestroy,
  signal,
} from '@angular/core';

import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BreadcrumbService } from '../../../root-services/breadcrumb.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChatSidebarService } from '../../../features/chat/services/chat-sidebar.service';
import { WorkspaceSidebarStateService } from '../../../features/workspace-sidebar/workspace-sidebar-state.service';
import { NavigationContextService } from '../../../root-services/navigation-context.service';

/**
 * BreadcrumbComponent
 *
 * Displays breadcrumb navigation trail with integrated mobile hamburger menu
 * Integrates with BreadcrumbService for reactive breadcrumb state
 * Provides clickable navigation back through hierarchy
 *
 * Responsive behavior:
 * - Desktop: Full breadcrumb trail only
 * - Mobile: Hamburger menu + condensed breadcrumb trail (on chat and workspace routes)
 * - Hamburger menu shown on mobile for both chat routes and workspace routes
 */
@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss',
  host: {
    '[class.on-chat-route]': 'isOnChatRoute()',
    '[class.on-workspace-route]': 'isOnWorkspaceRoute()',
  },
})
export class BreadcrumbComponent implements OnDestroy {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly router = inject(Router);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly chatSidebarService = inject(ChatSidebarService);
  private readonly workspaceSidebarStateService = inject(
    WorkspaceSidebarStateService,
  );
  private readonly navContextService = inject(NavigationContextService);

  private readonly breakpointSubscription?: Subscription;

  // Reactive breadcrumbs from service
  breadcrumbs = this.breadcrumbService.breadcrumbs;

  // Check if breadcrumbs should be displayed
  hasBreadcrumbs = computed(() => this.breadcrumbs().length > 0);

  /**
   * Route context signals — direct aliases to NavigationContextService.
   * The service owns route detection; this component only reads context.
   * Exposed as writable signals so tests can inject context without a real router.
   */
  isOnChatRoute = this.navContextService.isChatContext;

  isOnWorkspaceRoute = this.navContextService.isWorkspaceContext;

  // Track if we're on mobile (for hamburger menu display)
  isMobile = signal(false);

  constructor() {
    // Observe mobile breakpoint
    this.breakpointSubscription = this.breakpointObserver
      .observe([
        Breakpoints.HandsetPortrait,
        Breakpoints.HandsetLandscape,
        Breakpoints.TabletPortrait,
      ])
      .subscribe((result) => {
        this.isMobile.set(result.matches);
      });
  }

  ngOnDestroy(): void {
    // Clean up breakpoint subscription to prevent memory leaks
    this.breakpointSubscription?.unsubscribe();
  }

  /**
   * Determine if hamburger menu should be shown
   * Show on mobile for workspace routes (to toggle workspace sidebar)
   * Show on mobile for chat routes (to toggle chat sidebar)
   * Workspace sidebar is always visible on chat pages, so workspace hamburger not needed there
   */
  shouldShowHamburger(): boolean {
    return (
      this.isMobile() && (this.isOnChatRoute() || this.isOnWorkspaceRoute())
    );
  }

  /**
   * Handle hamburger menu toggle
   * Toggles appropriate sidebar based on current route
   */
  onToggleSidebar(): void {
    if (this.isOnChatRoute()) {
      this.chatSidebarService.toggleSidebar();
    } else if (this.isOnWorkspaceRoute()) {
      this.workspaceSidebarStateService.toggleSidebar();
    }
  }

  /**
   * Handle breadcrumb click navigation
   */
  onBreadcrumbClick(event: Event, index: number): void {
    const breadcrumb = this.breadcrumbs()[index];

    // Only navigate if clickable and has route
    if (!breadcrumb.clickable || !breadcrumb.route) {
      event.preventDefault();
      return;
    }

    // Navigate to breadcrumb route with query params if provided
    if (breadcrumb.queryParams) {
      this.router.navigate([breadcrumb.route], {
        queryParams: breadcrumb.queryParams,
      });
    } else {
      this.router.navigate([breadcrumb.route]);
    }
  }
}
